import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Camera } from "lucide-react";

/**
 * /anzeige/ambulanter-pflegeassistent
 *
 * Drei Editorial-Poster mit KI-generierten, emotional-warmen Custom-Fotos
 * (Gemini Nano Banana, Kinfolk-Aesthetik) — kein Pflegeklischee.
 * Alle im 4:5 Portrait für Meta Feed.
 */

const IMG_WINDOW    = "/ads/editorial_window.png";    // Frau am Fenster, Kaffee
const IMG_DESK      = "/ads/editorial_desk.png";      // Still-life: Notizbuch + Kaffee
const IMG_BACKHEAD  = "/ads/editorial_backhead.png";  // Frau im Terra-Cardigan im warmen Flur

export default function AnzeigeAmbulanterPflegeassistent() {
  const [variant, setVariant] = useState("all");
  return (
    <div className="min-h-screen bg-espresso-deep text-cream-50">
      <div className="border-b border-white/10 bg-espresso-deep/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            data-testid="anzeige-back"
            className="inline-flex items-center gap-1.5 text-[13px] text-cream-100 hover:text-saffron"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> zurück
          </Link>
          <div className="flex items-center gap-1.5 flex-wrap">
            <VariantBtn active={variant === "all"} onClick={() => setVariant("all")} label="Alle" />
            <VariantBtn active={variant === "v1"} onClick={() => setVariant("v1")} label="V1 · Fenster" />
            <VariantBtn active={variant === "v2"} onClick={() => setVariant("v2")} label="V2 · Split" />
            <VariantBtn active={variant === "v3"} onClick={() => setVariant("v3")} label="V3 · Flur" />
          </div>
          <div className="text-[11px] text-cream-100/60 inline-flex items-center gap-1">
            <Camera className="h-3 w-3" /> 4:5 · Screenshot für Ad-Manager
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8 sm:mb-10">
            <div className="inline-flex items-center rounded-full border border-white/20 text-cream-100/80 text-[10px] font-bold tracking-widest uppercase px-3 py-1 mb-3">
              Editorial · maßgeschneiderte Bilder · kein Stock
            </div>
            <h1 className="font-display text-4xl sm:text-5xl leading-tight">
              Mit Emotion. <span className="text-saffron">Ohne Klischee.</span>
            </h1>
          </div>

          <div className={`grid ${variant === "all" ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 justify-items-center"} gap-6 sm:gap-8 items-start`}>
            {(variant === "all" || variant === "v1") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V1" name="Fenster · Kaffee" />
                <V1Fenster />
              </div>
            )}
            {(variant === "all" || variant === "v2") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V2" name="Notizbuch · Testimonial" />
                <V2Notizbuch />
              </div>
            )}
            {(variant === "all" || variant === "v3") && (
              <div className="w-full max-w-[420px] mx-auto">
                <PosterLabel n="V3" name="Flur · Full Bleed" />
                <V3Flur />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V1 · FENSTER — Foto oben (60%), Cream unten mit Editorial-Typo
 * Emotional durch nachdenklichen Portrait-Ausdruck + persönliche Copy.
 * ================================================================ */
function V1Fenster() {
  return (
    <div
      data-testid="ad-v1"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden bg-cream-50 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col group"
    >
      {/* Foto */}
      <div className="relative h-[58%] overflow-hidden">
        <img
          src={IMG_WINDOW}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[35%_center] transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
          draggable={false}
        />
        {/* leichter Terra-Farbstich für Brand-Kohärenz */}
        <div className="absolute inset-0 bg-terra/10 mix-blend-multiply pointer-events-none" />
        {/* dünne Grain-Layer */}
        <div
          className="absolute inset-0 opacity-[0.09] mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>\")" }}
        />

        {/* Editorial-Header oben auf dem Foto */}
        <div className="absolute top-4 left-5 right-5 flex items-start justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold text-cream-50 mix-blend-difference">
          <span>Dickies-Campus · Nº 07</span>
          <span>MMXXVI</span>
        </div>

        {/* schwebender Kicker auf der Bildkante */}
        <div className="absolute bottom-3 left-5 right-5 flex items-center justify-between gap-2">
          <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-cream-50 bg-espresso-deep/85 rounded-full px-2.5 py-1">
            für Inhaber &amp; PDL
          </span>
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.20em] text-espresso-deep bg-saffron rounded-full px-2.5 py-1">
            Neu · Pflege-KI 2026
          </span>
        </div>
      </div>

      {/* Cream-Content unten */}
      <div className="flex-1 relative px-6 pt-4 pb-5 flex flex-col">
        {/* dekorativer Terrabalken links */}
        <div className="absolute -top-[1px] left-6 h-[3px] w-14 bg-terra" />

        {/* Produkt-Kicker · Pflege-Signal */}
        <div className="flex items-center gap-2 mb-2">
          <PflegeKiMark />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em] text-terra">
            Pflege-KI · Ambulanter Dienst
          </span>
        </div>

        {/* Emotionale Headline */}
        <h2 className="font-display text-espresso-deep font-black tracking-[-0.02em] leading-[0.95] text-[34px] mb-2">
          Wann hattest du <br/>
          <span className="italic text-terra">zuletzt Sonntag?</span>
        </h2>
        <p className="text-[12px] leading-[1.45] text-sand-700 max-w-[300px] mb-3">
          Die <strong className="text-espresso-deep">Ambulante Pflegeassistenz</strong> übernimmt SIS,
          QPR-26 und MD-Vorbereitung — damit du am Sonntag nicht mehr an Montag denken musst.
        </p>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep text-cream-50 px-4 py-2 text-[12px] font-bold">
            Potenzial-Check
            <span className="inline-block w-1 h-1 rounded-full bg-saffron" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8.5px] uppercase tracking-[0.24em] font-bold text-espresso-deep/60">Zum Start</div>
            <div className="font-display text-[22px] leading-none font-black text-brand-darkred">
              −50 %
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V2 · NOTIZBUCH — Split. Links Still-Life Foto. Rechts Terra mit
 * Testimonial-Zitat für maximale Emotion.
 * ================================================================ */
function V2Notizbuch() {
  return (
    <div
      data-testid="ad-v2"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] flex flex-col"
    >
      {/* Foto oben (50%) */}
      <div className="relative h-[50%] overflow-hidden">
        <img
          src={IMG_DESK}
          alt=""
          className="w-full h-full object-cover"
          draggable={false}
        />
        <div className="absolute inset-0 bg-espresso-deep/10 mix-blend-multiply pointer-events-none" />

        {/* Pflege-KI Marker oben links */}
        <div className="absolute top-4 left-5 inline-flex items-center gap-1.5 rounded-full bg-cream-50/95 text-espresso-deep px-2.5 py-1 shadow">
          <PflegeKiMark />
          <span className="text-[9.5px] font-bold uppercase tracking-[0.24em]">
            Pflege-KI · SIS · QPR
          </span>
        </div>

        {/* Editorial Marker oben rechts auf Foto */}
        <div className="absolute top-4 right-5 text-[9px] font-bold uppercase tracking-[0.32em] text-cream-50 bg-espresso-deep/70 rounded-full px-2.5 py-1">
          Nº 07 · MMXXVI
        </div>
      </div>

      {/* Terra unten (50%) mit Testimonial */}
      <div className="relative flex-1 bg-terra text-cream-50 px-6 pt-5 pb-5 flex flex-col">
        {/* dünne Cream-Trennlinie oben */}
        <div className="absolute -top-[1px] left-0 right-0 h-[1px] bg-cream-50/40" />
        {/* Anführungszeichen als Deko */}
        <div className="absolute top-2 left-5 font-display text-[80px] leading-none text-saffron/40 select-none">
          „
        </div>

        <blockquote className="pl-8 pr-3 font-display italic text-[17px] leading-[1.15] text-cream-50">
          Zum ersten Mal seit Jahren habe ich Sonntagabend das Handy weggelegt — und die Pflegedoku war trotzdem fertig.
        </blockquote>

        <div className="pl-8 mt-2 text-[10.5px] uppercase tracking-[0.24em] font-bold text-cream-50/80">
          — Nina M., PDL Ambulanter Dienst
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="leading-tight">
            <div className="text-[9px] uppercase tracking-[0.28em] font-bold text-cream-50/70 mb-0.5">
              Ambulante Pflegeassistenz
            </div>
            <div className="font-display font-black text-[22px] leading-none tracking-tight">
              Jetzt −50 %
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-cream-50 text-espresso-deep px-4 py-2 text-[12px] font-bold shadow">
            Potenzial-Check
            <span className="inline-block w-1 h-1 rounded-full bg-terra" />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * V3 · FLUR — Full-Bleed Foto. Cream-Typografie mit warmem Overlay.
 * Emotional durch back-of-head Perspektive: "das bin ich".
 * ================================================================ */
function V3Flur() {
  return (
    <div
      data-testid="ad-v3"
      className="relative w-full aspect-[4/5] rounded-2xl overflow-hidden shadow-[0_25px_60px_-20px_rgba(0,0,0,0.7)] bg-espresso-deep group"
    >
      {/* Full-Bleed Foto */}
      <img
        src={IMG_BACKHEAD}
        alt=""
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.03]"
        draggable={false}
      />
      {/* Warmer Espresso-Deep Vignette-Gradient bottom + top */}
      <div className="absolute inset-0 bg-gradient-to-b from-espresso-deep/40 via-transparent to-espresso-deep/95" />
      {/* Terra-Farbwash für Brand-Kohärenz */}
      <div className="absolute inset-0 bg-terra/8 mix-blend-multiply pointer-events-none" />
      {/* Grain */}
      <div
        className="absolute inset-0 opacity-[0.12] pointer-events-none mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")" }}
      />

      {/* Top Editorial Bar */}
      <div className="absolute top-5 left-5 right-5 flex items-start justify-between text-[9.5px] uppercase tracking-[0.28em] font-bold text-cream-50/85">
        <span>Dickies-Campus · Nº 07</span>
        <span className="text-saffron">MMXXVI</span>
      </div>

      {/* Prominenter Pflege-KI Marker · unter der Top-Bar */}
      <div className="absolute top-14 left-5">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-3 py-1.5 shadow-lg">
          <PflegeKiMark />
          <span className="text-[10px] font-black uppercase tracking-[0.22em]">
            Pflege-KI für ambulante Dienste
          </span>
        </div>
      </div>

      {/* Bottom Overlay Content */}
      <div className="absolute inset-x-0 bottom-0 p-6 pb-6">
        <div className="inline-flex items-center gap-1.5 rounded-full bg-espresso-deep/80 backdrop-blur-sm text-saffron px-2.5 py-1 text-[9.5px] font-bold uppercase tracking-[0.24em] mb-3">
          für PDL · SIS · QPR-26 · MD-Simulation
        </div>

        <h2 className="font-display font-black tracking-[-0.03em] leading-[0.86] text-cream-50 drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
          <span className="block text-[42px]">Nach Hause.</span>
          <span className="block text-[42px] italic">Ohne Pflegedoku</span>
          <span className="block text-[42px] underline decoration-[3px] decoration-saffron underline-offset-[8px]">im Kopf.</span>
        </h2>

        <p className="mt-3 max-w-[280px] text-[12.5px] text-cream-100/85 leading-relaxed">
          <strong className="text-cream-50">Ambulante Pflegeassistenz</strong> — die KI, die
          nach dir aufräumt. <span className="text-saffron font-black">−50 % zum Start.</span>
        </p>

        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-saffron text-espresso-deep px-4 py-2 text-[12px] font-black shadow">
            Potenzial-Check anfragen
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-darkred" />
          </div>
          <div className="text-right leading-tight">
            <div className="text-[8px] uppercase tracking-[0.28em] font-bold text-cream-50/60">Web</div>
            <div className="text-[10.5px] font-black tracking-tight text-cream-50">dickies-helden.de</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Kleiner Pflege-Kreuz-Mark · signalisiert "Pflege" ohne Klischee.
 * Wird überall dort eingesetzt wo wir Pflege-Kontext brauchen. */
function PflegeKiMark({ size = 14 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="shrink-0"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="11" fill="currentColor" opacity="0.18" />
      <path
        d="M10 5h4v5h5v4h-5v5h-4v-5H5v-4h5V5z"
        fill="currentColor"
      />
    </svg>
  );
}

function PosterLabel({ n, name }) {
  return (
    <div className="mb-3 flex items-center gap-2 text-cream-50">
      <span className="inline-flex items-center justify-center h-7 min-w-7 px-2 rounded-full bg-saffron text-espresso-deep font-display font-black text-[13px]">
        {n}
      </span>
      <span className="font-display text-lg">{name}</span>
    </div>
  );
}

function VariantBtn({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      className={`rounded-full px-3 py-1 text-[12px] font-semibold transition-colors ${
        active
          ? "bg-saffron text-espresso-deep"
          : "border border-white/20 text-cream-100/80 hover:border-saffron/60"
      }`}
    >
      {label}
    </button>
  );
}
